import React, { useState, useEffect } from 'react';
import { X, TrendingUp } from 'lucide-react';
import { API_BASE_URL } from '../../config/api';
import { getAuthHeaders } from '../../utils/authUtils';

interface HistoryChartModalProps {
    petId: string;
    section: 'vitals' | 'objective';
    vitalKey: string;
    vitalLabel: string;
    onClose: () => void;
}

interface VitalPoint {
    date: string;
    value: number;
    originalValue?: any;
}

const HistoryChartModal: React.FC<HistoryChartModalProps> = ({ petId, section, vitalKey, vitalLabel, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<VitalPoint[]>([]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await fetch(`${API_BASE_URL}/api/v1/pets/${petId}/${section}`, {
                    headers: getAuthHeaders(),
                });

                if (!response.ok) throw new Error(`Failed to fetch ${section} history`);

                const history = await response.json();

                // Process data
                const points: VitalPoint[] = history
                    .map((item: any) => {
                        // Case-insensitive lookup
                        const keys = Object.keys(item.vitals);
                        const matchingKey = keys.find(k => k.toLowerCase() === vitalKey.toLowerCase());
                        let val = matchingKey ? item.vitals[matchingKey] : undefined;
                        let numVal = parseFloat(val);

                        // Handle ranges like "1-2s" -> 1.5
                        if (isNaN(numVal) && typeof val === 'string' && val.includes('-')) {
                            const parts = val.split('-').map(p => parseFloat(p.replace(/[^0-9.]/g, '')));
                            if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
                                numVal = (parts[0] + parts[1]) / 2;
                            }
                        }

                        // Handle simple text with numbers like "<2s" -> 2
                        if (isNaN(numVal) && typeof val === 'string') {
                            const extracted = parseFloat(val.replace(/[^0-9.]/g, ''));
                            if (!isNaN(extracted)) {
                                numVal = extracted;
                            }
                        }

                        return {
                            date: item.date,
                            value: numVal,
                            originalValue: val // Store original for tooltip
                        };
                    })
                    .sort((a: VitalPoint, b: VitalPoint) => new Date(a.date).getTime() - new Date(b.date).getTime());

                setData(points);
            } catch (err: any) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [petId, section, vitalKey]);

    const renderTable = () => (
        <div className="overflow-auto h-full">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {data.map((point, i) => (
                        <tr key={i}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {new Date(point.date).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {point.originalValue?.toString() || '-'}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    // Simple SVG Chart implementation
    const renderChart = () => {
        const numericPoints = data.filter(d => !isNaN(d.value));

        // If we have no numeric data, or very little, show a table instead
        if (numericPoints.length === 0) {
            return renderTable();
        }

        if (numericPoints.length < 2) {
            return (
                <div className="h-full flex flex-col">
                    <div className="flex-1">{renderTable()}</div>
                    <p className="text-center text-gray-500 py-2 text-sm">Not enough numeric data to chart</p>
                </div>
            );
        }

        const width = 600;
        const height = 300;
        const padding = 40;

        const values = numericPoints.map(d => d.value);
        const minVal = Math.min(...values);
        const maxVal = Math.max(...values);
        const range = maxVal - minVal || 1; // avoid division by zero

        // Scales
        const xScale = (index: number) => padding + (index / (numericPoints.length - 1)) * (width - 2 * padding);
        const yScale = (val: number) => height - padding - ((val - minVal) / range) * (height - 2 * padding);

        const points = numericPoints.map((d, i) => `${xScale(i)},${yScale(d.value)}`).join(' ');

        return (
            <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
                {/* Grid lines */}
                {[0, 0.25, 0.5, 0.75, 1].map(t => {
                    const y = height - padding - t * (height - 2 * padding);
                    const val = minVal + t * range;
                    return (
                        <g key={t}>
                            <line x1={padding} y1={y} x2={width - padding} y2={y} stroke="#e5e7eb" strokeDasharray="4" />
                            <text x={padding - 10} y={y + 4} textAnchor="end" className="text-xs fill-gray-400">{val.toFixed(1)}</text>
                        </g>
                    );
                })}

                {/* Line */}
                <polyline fill="none" stroke="#3b82f6" strokeWidth="2" points={points} />

                {/* Points */}
                {numericPoints.map((d, i) => (
                    <g key={i} className="group">
                        <circle cx={xScale(i)} cy={yScale(d.value)} r="4" className="fill-blue-600 hover:r-6 transition-all cursor-pointer" />
                        {/* Tooltip */}
                        <g className="opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                            <rect x={xScale(i) - 60} y={yScale(d.value) - 40} width="120" height="30" rx="4" fill="black" fillOpacity="0.8" />
                            <text x={xScale(i)} y={yScale(d.value) - 20} textAnchor="middle" className="text-xs fill-white font-medium">
                                {new Date(d.date).toLocaleDateString()} : {d.originalValue || d.value}
                            </text>
                        </g>
                    </g>
                ))}

                {/* X Axis Labels (show first and last) */}
                <text x={padding} y={height - 10} textAnchor="start" className="text-xs fill-gray-500">{new Date(numericPoints[0].date).toLocaleDateString()}</text>
                <text x={width - padding} y={height - 10} textAnchor="end" className="text-xs fill-gray-500">{new Date(numericPoints[numericPoints.length - 1].date).toLocaleDateString()}</text>
            </svg>
        );
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl animate-fade-in">
                <div className="flex items-center justify-between p-6 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="bg-blue-100 p-2 rounded-lg">
                            <TrendingUp className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">{vitalLabel} History</h2>
                            <p className="text-sm text-gray-500">Historical trends for this pet</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 h-[400px]">
                    {loading ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    ) : error ? (
                        <div className="flex items-center justify-center h-full text-red-500">
                            {error}
                        </div>
                    ) : (
                        renderChart()
                    )}
                </div>
            </div>
        </div>
    );
};

export default HistoryChartModal;
